import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react'
import * as THREE from 'three'
import { Canvas, useThree } from '@react-three/fiber'
import type { RenderRecipe } from '../lib/types'
import { unpackColor } from '../lib/parseWrapper'

export type PresetView = 'front' | 'back' | 'spine'

export interface SceneApi {
  setView(view: PresetView): void
  resetView(): void
  screenshot(): string | null
  /** Spin the book by an azimuth delta (degrees) — used by hotkeys. */
  rotate(dAzimuthDeg: number, dPolarDeg?: number): void
  /** Multiply zoom distance (e.g. 0.9 = zoom in, 1.1 = zoom out). */
  zoom(factor: number): void
}

export interface LightControls {
  ambient: number
  diffuse: number
  azimuthDeg: number
  elevationDeg: number
}

export interface Orientation {
  /** Base rotation applied to the model, in degrees. */
  rotationDeg: [number, number, number]
  /** Euler application order (Chili/Away3D differs from three's default XYZ). */
  order: THREE.EulerOrder
}

interface CoverSceneProps {
  recipe: RenderRecipe
  /** The prepared COLLADA scene (from buildModel). */
  group: THREE.Group
  orientation: Orientation
  light: LightControls
  autoSpin: boolean
  /** Weak device (e.g. Chromebook): cap spin FPS, pixel ratio, shadow resolution. */
  lowPower: boolean
  apiRef: RefObject<SceneApi | null>
  onUserInteract?: () => void
}

interface Measure {
  /** Largest horizontal extent (max of x, z). */
  footprint: number
  /** Half the bounding-box diagonal (for sizing the shadow frustum + camera). */
  radius: number
  /** Vertical extent (book height) — used to aim the camera at the book center. */
  height: number
}

const SPIN_SPEED = 2.2 // matches the old OrbitControls autoRotateSpeed feel
const DEFAULT_ELEV_DEG = 3 // default ~level with the book
const ELEV_MIN_DEG = -70 // negative = camera below the book, looking up at the bottom
const ELEV_MAX_DEG = 80 // looking down at the top
const ZOOM_MIN = 0.45
const ZOOM_MAX = 2.2
const DEFAULT_YAW = -0.5 // book turned ~29° so the default view shows front + a side
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))

// Book yaw (rotation about its vertical axis) for each preset button.
const PRESET_YAW: Record<PresetView, number> = {
  front: 0,
  back: Math.PI,
  spine: Math.PI / 2, // the bound edge (opposite the fore-edge)
}

/**
 * Applies the per-type orientation, recenters the model so its base rests on y=0
 * (centered in x/z) so it spins in place over the shadow, and marks meshes as shadow
 * casters. Reports its measured footprint/radius/height.
 */
function GroundedModel({
  group,
  orientation,
  modelKey,
  onMeasure,
}: {
  group: THREE.Group
  orientation: Orientation
  modelKey: string
  onMeasure: (m: Measure) => void
}) {
  const ref = useRef<THREE.Group>(null!)
  const [rx, ry, rz] = orientation.rotationDeg
  useLayoutEffect(() => {
    const g = ref.current
    if (!g) return
    g.position.set(0, 0, 0)
    g.updateWorldMatrix(true, true)
    const box = new THREE.Box3().setFromObject(g)
    const center = box.getCenter(new THREE.Vector3())
    const size = box.getSize(new THREE.Vector3())
    g.position.set(-center.x, -box.min.y, -center.z)
    g.traverse((o) => {
      if ((o as THREE.Mesh).isMesh) (o as THREE.Mesh).castShadow = true
    })
    onMeasure({ footprint: Math.max(size.x, size.z), radius: 0.5 * size.length(), height: size.y })
    // Depend on orientation *values* (the object identity changes every render).
  }, [group, modelKey, rx, ry, rz, orientation.order, onMeasure])

  return (
    <group
      ref={ref}
      rotation={[
        THREE.MathUtils.degToRad(orientation.rotationDeg[0]),
        THREE.MathUtils.degToRad(orientation.rotationDeg[1]),
        THREE.MathUtils.degToRad(orientation.rotationDeg[2]),
        orientation.order,
      ]}
    >
      <primitive object={group} />
    </group>
  )
}

/**
 * Disables automatic per-frame shadow updates (the controller re-bakes on demand,
 * throttled) and forces a re-bake whenever the light or model size changes.
 */
function ShadowSync({ dep }: { dep: string }) {
  const gl = useThree((s) => s.gl)
  const invalidate = useThree((s) => s.invalidate)
  useEffect(() => {
    gl.shadowMap.autoUpdate = false
  }, [gl])
  useEffect(() => {
    gl.shadowMap.needsUpdate = true
    invalidate()
  }, [gl, invalidate, dep])
  return null
}

/**
 * Turntable + view controls. Horizontal drag (and presets / auto-spin / hotkeys)
 * rotates the BOOK about its vertical axis; vertical drag tilts the camera elevation
 * so you can look at the top/bottom. The camera azimuth and the light stay fixed, so
 * the lit side is consistent and the shadow stays anchored under the book.
 *
 * Runs an FPS-capped rAF loop only while moving (idle ≈ 0 CPU), and re-bakes the
 * shadow at ~10 Hz during motion instead of every frame (the spin CPU fix).
 */
function TurntableController({
  measure,
  turntableRef,
  autoSpin,
  lowPower,
  apiRef,
  onUserInteract,
}: {
  measure: Measure
  turntableRef: RefObject<THREE.Group | null>
  autoSpin: boolean
  lowPower: boolean
  apiRef: RefObject<SceneApi | null>
  onUserInteract?: () => void
}) {
  const camera = useThree((s) => s.camera as THREE.PerspectiveCamera)
  const scene = useThree((s) => s.scene)
  const gl = useThree((s) => s.gl)
  const invalidate = useThree((s) => s.invalidate)
  const yaw = useRef({ cur: DEFAULT_YAW, target: DEFAULT_YAW })
  const elev = useRef({ cur: DEFAULT_ELEV_DEG, target: DEFAULT_ELEV_DEG })
  const zoom = useRef({ cur: 1, target: 1 })
  const dragging = useRef(false)

  useEffect(() => {
    const dom = gl.domElement
    const fps = lowPower ? 24 : 48
    const interval = 1000 / fps
    const SPIN = ((2 * Math.PI) / 60) * SPIN_SPEED
    const halfFov = THREE.MathUtils.degToRad(camera.fov / 2)
    const baseDist = (measure.radius / Math.sin(halfFov)) * 1.15
    const target = new THREE.Vector3(0, measure.height / 2, 0)

    const placeCamera = () => {
      const el = THREE.MathUtils.degToRad(elev.current.cur)
      const dist = baseDist * zoom.current.cur
      const dir = new THREE.Vector3(0, Math.sin(el), Math.cos(el)).normalize()
      camera.position.copy(target).addScaledVector(dir, dist)
      camera.up.set(0, 1, 0)
      camera.lookAt(target)
      camera.updateProjectionMatrix()
    }

    let raf = 0
    let running = false
    let lastTick = 0
    let lastFrame = 0
    let shadowAcc = 0

    const tick = (t: number) => {
      if (t - lastTick < interval) {
        raf = requestAnimationFrame(tick)
        return
      }
      lastTick = t
      const dt = Math.min(0.05, (t - lastFrame) / 1000)
      lastFrame = t
      let moving = false
      if (autoSpin && !dragging.current) {
        yaw.current.target -= SPIN * dt
        moving = true
      }
      const damp = Math.min(1, dt * 10)
      for (const s of [yaw.current, elev.current, zoom.current]) {
        const d = s.target - s.cur
        if (Math.abs(d) > 1e-4) {
          s.cur += d * damp
          moving = true
        } else {
          s.cur = s.target
        }
      }
      if (turntableRef.current) turntableRef.current.rotation.y = yaw.current.cur
      placeCamera()
      if (moving) {
        // Re-bake the shadow ~5x/sec while moving instead of every frame.
        shadowAcc += dt
        if (shadowAcc >= 0.2) {
          gl.shadowMap.needsUpdate = true
          shadowAcc = 0
        }
        invalidate()
      } else {
        gl.shadowMap.needsUpdate = true // final accurate bake once settled
        invalidate()
      }
      if (moving || autoSpin || dragging.current) raf = requestAnimationFrame(tick)
      else running = false
    }
    const start = () => {
      if (running) return
      running = true
      lastTick = performance.now() - interval
      lastFrame = performance.now()
      raf = requestAnimationFrame(tick)
    }

    let lastX = 0
    let lastY = 0
    const onDown = (e: PointerEvent) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return
      dragging.current = true
      lastX = e.clientX
      lastY = e.clientY
      try {
        dom.setPointerCapture(e.pointerId)
      } catch {
        /* ignore */
      }
      dom.style.cursor = 'grabbing'
      onUserInteract?.()
      start()
    }
    const onMove = (e: PointerEvent) => {
      if (!dragging.current) return
      const dx = e.clientX - lastX
      const dy = e.clientY - lastY
      lastX = e.clientX
      lastY = e.clientY
      yaw.current.target -= dx * 0.01 // horizontal → spin the book
      elev.current.target = clamp(elev.current.target - dy * 0.3, ELEV_MIN_DEG, ELEV_MAX_DEG) // vertical → tilt view
      start()
    }
    const onUp = (e: PointerEvent) => {
      dragging.current = false
      dom.style.cursor = 'grab'
      try {
        dom.releasePointerCapture(e.pointerId)
      } catch {
        /* ignore */
      }
    }
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      zoom.current.target = clamp(zoom.current.target * Math.exp(e.deltaY * 0.0012), ZOOM_MIN, ZOOM_MAX)
      start()
    }

    dom.style.touchAction = 'none'
    dom.style.cursor = 'grab'
    dom.addEventListener('pointerdown', onDown)
    dom.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    dom.addEventListener('wheel', onWheel, { passive: false })

    apiRef.current = {
      setView: (v) => {
        yaw.current.target = PRESET_YAW[v]
        start()
      },
      resetView: () => {
        yaw.current.target = DEFAULT_YAW
        elev.current.target = DEFAULT_ELEV_DEG
        zoom.current.target = 1
        start()
      },
      rotate: (dAz, dPolar = 0) => {
        yaw.current.target += THREE.MathUtils.degToRad(dAz)
        elev.current.target = clamp(elev.current.target + dPolar, ELEV_MIN_DEG, ELEV_MAX_DEG)
        start()
      },
      zoom: (factor) => {
        zoom.current.target = clamp(zoom.current.target * factor, ZOOM_MIN, ZOOM_MAX)
        start()
      },
      screenshot: () => {
        gl.shadowMap.needsUpdate = true
        gl.render(scene, camera)
        try {
          return dom.toDataURL('image/png')
        } catch {
          return null
        }
      },
    }

    placeCamera()
    gl.shadowMap.needsUpdate = true
    start() // settle the initial pose / begin auto-spin

    return () => {
      cancelAnimationFrame(raf)
      dom.removeEventListener('pointerdown', onDown)
      dom.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      dom.removeEventListener('wheel', onWheel)
      apiRef.current = null
    }
  }, [autoSpin, lowPower, gl, scene, camera, invalidate, turntableRef, apiRef, onUserInteract, measure])

  return null
}

export function CoverScene({
  recipe,
  group,
  orientation,
  light,
  autoSpin,
  lowPower,
  apiRef,
  onUserInteract,
}: CoverSceneProps) {
  const turntableRef = useRef<THREE.Group>(null!)
  const [measure, setMeasure] = useState<Measure>({ footprint: 12, radius: 14, height: 18 })
  const onMeasure = useCallback((m: Measure) => setMeasure(m), [])
  const modelKey = group.uuid

  const lightDist = Math.max(20, measure.radius * 4)
  const keyPos = useMemo<[number, number, number]>(() => {
    const az = THREE.MathUtils.degToRad(light.azimuthDeg)
    const el = THREE.MathUtils.degToRad(light.elevationDeg)
    const cosEl = Math.cos(el)
    return [Math.sin(az) * cosEl * lightDist, Math.sin(el) * lightDist, Math.cos(az) * cosEl * lightDist]
  }, [light.azimuthDeg, light.elevationDeg, lightDist])
  const lightColor = useMemo(() => {
    const [r, g, b] = unpackColor(recipe.light?.color ?? 0xffffff)
    return new THREE.Color(r, g, b)
  }, [recipe])
  const shadowSize = lowPower ? 512 : 1024

  return (
    <Canvas
      flat
      shadows
      dpr={lowPower ? 1 : [1, 1.5]}
      frameloop="demand"
      gl={{ antialias: true, alpha: true, preserveDrawingBuffer: true }}
      camera={{ position: [0, 8, 60], fov: 35 }}
    >
      <ambientLight intensity={light.ambient} />
      {/* Key light — world-fixed, casts the shadow. Stays put as the book turns. */}
      <directionalLight
        position={keyPos}
        intensity={light.diffuse * 1.7}
        color={lightColor}
        castShadow
        shadow-mapSize-width={shadowSize}
        shadow-mapSize-height={shadowSize}
        shadow-radius={5}
        shadow-bias={-0.0006}
        shadow-camera-near={0.1}
        shadow-camera-far={lightDist * 2.2}
        shadow-camera-left={-measure.radius}
        shadow-camera-right={measure.radius}
        shadow-camera-top={measure.radius}
        shadow-camera-bottom={-measure.radius}
      />
      {/* Fill light (no shadow) — cross-lighting so the cover's curves read. */}
      <directionalLight
        position={[-lightDist * 0.7, lightDist * 0.55, lightDist * 0.6]}
        intensity={light.diffuse * 0.5}
        color={lightColor}
      />

      {/* Turntable: the book rotates here; the camera does not. */}
      <group ref={turntableRef}>
        <GroundedModel group={group} orientation={orientation} modelKey={modelKey} onMeasure={onMeasure} />
      </group>

      {/* Fixed ground plane that only shows the cast shadow. */}
      <mesh rotation-x={-Math.PI / 2} position-y={0} receiveShadow>
        <planeGeometry args={[measure.footprint * 6, measure.footprint * 6]} />
        <shadowMaterial transparent opacity={0.32} />
      </mesh>

      <ShadowSync dep={`${light.azimuthDeg}:${light.elevationDeg}:${light.diffuse}:${Math.round(measure.radius * 100)}`} />
      <TurntableController
        measure={measure}
        turntableRef={turntableRef}
        autoSpin={autoSpin}
        lowPower={lowPower}
        apiRef={apiRef}
        onUserInteract={onUserInteract}
      />
    </Canvas>
  )
}
